import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Message, ToolCall, addMessage, setLoading, clearChat, toggleToolCall } from '../../../actions/chatActions';
import { AgentType, AnalyticsContext } from './types';
import { BI_ANALYTICS_URL_PATTERN } from './constants';
import { 
  createUserMessage, 
  createAiMessage, 
  handleSupportAgent, 
  handleBiAgent, 
  handleChatReset 
} from './agentHandlers';

export const useAgentDetection = () => {
  const [selectedAgent, setSelectedAgent] = useState<AgentType>(AgentType.SUPPORT);
  const [showBI, setShowBI] = useState(false);

  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes(BI_ANALYTICS_URL_PATTERN)) {
      setSelectedAgent(AgentType.BI);
      setShowBI(true);
    } else {
      setSelectedAgent(AgentType.SUPPORT);
      setShowBI(false);
    }
  }, []);

  return { selectedAgent, setSelectedAgent, showBI };
};

export const useChatState = () => {
  const dispatch = useDispatch();
  const chatState = useSelector((state: any) => state.chat);
  
  const [localMessages, setLocalMessages] = useState<(Message | ToolCall)[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  
  const messages = chatState?.messages || localMessages;
  const isLoading = chatState?.isLoading || localLoading;
  const executionLayers = chatState?.executionLayers || [];
  const currentLayerIndex = chatState?.currentLayerIndex || 0;
  const allResults = chatState?.allResults || {};

  const addMessageToChat = useCallback((message: Message | ToolCall) => {
    if (chatState) {
      dispatch(addMessage(message));
    } else {
      setLocalMessages(prev => [...prev, message]);
    }
  }, [chatState, dispatch]);

  const setLoadingState = useCallback((loading: boolean) => {
    if (chatState) {
      dispatch(setLoading(loading));
    } else {
      setLocalLoading(loading);
    }
  }, [chatState, dispatch]);

  const clearChatMessages = useCallback(() => {
    if (chatState) {
      dispatch(clearChat());
    } else {
      setLocalMessages([]);
      setLocalLoading(false);
    }
  }, [chatState, dispatch]);

  const toggleToolCallExpansion = useCallback((messageIndex: number) => {
    if (chatState) {
      dispatch(toggleToolCall(messageIndex));
    } else {
      setLocalMessages(prev => 
        prev.map((msg, index) =>
          index === messageIndex
            ? { ...msg, isExpanded: !msg.isExpanded }
            : msg
        )
      );
    }
  }, [chatState, dispatch]);

  return {
    messages,
    isLoading,
    executionLayers,
    currentLayerIndex,
    allResults,
    addMessageToChat,
    setLoadingState,
    clearChatMessages,
    toggleToolCallExpansion
  };
};

export const useMessageHandling = (
  selectedAgent: AgentType,
  analyticsContext: AnalyticsContext,
  addMessageToChat: (message: Message | ToolCall) => void,
  setLoadingState: (loading: boolean) => void,
  messages: (Message | ToolCall)[]
) => {
  const dispatch = useDispatch();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const runIdRef = useRef(0);

  const handleSendMessage = useCallback(async (messageContent?: string, inputValue?: string) => {
    const content = messageContent || inputValue;
    if (!content?.trim()) return;

    // Start a new run and capture the id in this closure
    runIdRef.current += 1;
    const runId = runIdRef.current;

    const userMessage = createUserMessage(content);
    addMessageToChat(userMessage);
    setLoadingState(true);

    try {
      if (selectedAgent === AgentType.SUPPORT) {
        const aiMessage = await handleSupportAgent(content, messages, userTimezone);
        if (runId !== runIdRef.current) return;
        addMessageToChat(aiMessage);
      } else {
        const guardedOnToolCallComplete = (toolCall: ToolCall) => {
          if (runId === runIdRef.current) {
            addMessageToChat(toolCall);
          }
        };
        const { message: aiMessage } = await handleBiAgent(
          content,
          messages,
          analyticsContext,
          guardedOnToolCallComplete,
          dispatch
        );
        if (runId !== runIdRef.current) return;
        addMessageToChat(aiMessage);
      }
    } catch (error) {
      console.log({error});
      if (runId === runIdRef.current) {
        const errorMessage = createAiMessage(
          'Sorry, I encountered an error processing your request. Please try again.'
        );
        addMessageToChat(errorMessage);
      }
    } finally {
      if (runId === runIdRef.current) {
        setLoadingState(false);
      }
    }
  }, [selectedAgent, analyticsContext, addMessageToChat, setLoadingState, messages, userTimezone, dispatch]);

  return { handleSendMessage };
};

export const useScrollToBottom = (messages: (Message | ToolCall)[]) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  return { messagesEndRef, scrollToBottom };
};

export const useIframeMessageListener = (clearChatMessages: () => void) => {
  const handleResetState = useCallback(() => {
    clearChatMessages();
    handleChatReset();
  }, [clearChatMessages]);

  return { handleResetState };
}; 